// Treasury → User Wallets: which currency's wallets the directory shows
// (SM-494), and the one rule that makes this page different from every
// other currency filter in the console.
//
// A user holds one wallet row per currency since ledger 0025, so an
// unfiltered directory lists each person up to three times. That is a
// legitimate view — reconciling one holder across all their money needs
// it — but it is the wrong thing to open on: the operator work that
// starts here is nearly always about the currency people actually top
// up and cash out, which is GOLD. So this page's default is GOLD rather
// than "no filter".
//
// That inverts the usual search-param convention, and the encoding has
// to say so explicitly. Absent means "the operator has expressed no
// preference" → GOLD; "all" is a CHOICE and gets written into the URL,
// because if it were encoded as absence a refresh would silently put
// the operator back on GOLD while their picker had said All.

import { CurrencyFilterSelect } from "@/components/currency/CurrencyFilterSelect";
import { useRegistryCurrencyCodes } from "@/components/currency/registryCurrencies";
import { isCurrencyCode, type CurrencyCode } from "@/currency";

/** The currency the directory opens on when the URL says nothing. */
export const DEFAULT_WALLET_CURRENCY: CurrencyCode = "GOLD";

/** The search-param value for the combined, unfiltered list. */
export const WALLET_CURRENCY_ALL = "all";

/** What `?currency=` may say: one currency, or the explicit "all". */
export type WalletCurrencySearch = CurrencyCode | typeof WALLET_CURRENCY_ALL;

/**
 * Read the filter out of untrusted text (a URL search param).
 *
 * Junk reads as ABSENT rather than as "all" — absent is the page's
 * default, so a mistyped URL lands an operator on the same view a plain
 * /treasury does instead of on a three-currency list they did not ask
 * for. The lowercase "all" is deliberate: it is a UI sentinel, not a
 * wire code, and no currency code is lowercase.
 */
export function parseWalletCurrencySearch(raw: unknown): WalletCurrencySearch | undefined {
  if (raw === WALLET_CURRENCY_ALL) return WALLET_CURRENCY_ALL;
  return isCurrencyCode(raw) ? raw : undefined;
}

/**
 * The currency the LIST is actually filtered by: undefined means no
 * `?currency=` goes to the server, which is every wallet.
 *
 * The whole default lives here, in one total function, so the query
 * key, the loader's prefetch and the picker's value cannot disagree
 * about what an absent param means — a disagreement that shows up as
 * the table and the control above it describing different result sets.
 */
export function walletListCurrency(
  search: WalletCurrencySearch | undefined,
): CurrencyCode | undefined {
  if (search === undefined) return DEFAULT_WALLET_CURRENCY;
  return search === WALLET_CURRENCY_ALL ? undefined : search;
}

/** The inverse: what to write into the URL for a picked currency. */
export function walletCurrencySearchFor(next: CurrencyCode | undefined): WalletCurrencySearch {
  return next ?? WALLET_CURRENCY_ALL;
}

/**
 * The picker. Offers every currency the registry carries — a wallet
 * exists in all of them, unlike a position's stake leg — in the
 * operator's configured order, plus the combined list.
 */
export function WalletCurrencyFilter({
  value,
  onChange,
  className,
}: {
  value: CurrencyCode | undefined;
  onChange: (next: CurrencyCode | undefined) => void;
  className?: string;
}) {
  const codes = useRegistryCurrencyCodes();
  return (
    <CurrencyFilterSelect
      codes={codes}
      value={value}
      onChange={onChange}
      allValue={WALLET_CURRENCY_ALL}
      className={className}
    />
  );
}
